import { Link } from 'react-router-dom';
import { Card } from '@thegpvc/ui';
import { text, cx } from '../../styles/theme';
import { Eyebrow } from './WorkDeliveredCard';
import { HeatmapLegend } from '../HeatmapLegend';
import { computeThresholds, getLevel, LEVEL_CLASSES } from '../../utils/heatmap';
import type { GetProjectHeatmapResponse } from '../../types/api';

const MAX_PROJECT_ROWS = 7;

interface Row {
  project: string;
  isOther: boolean;
  otherCount: number;
  byDay: Map<number, number>;
  total: number;
}

/**
 * WorkHeatmap answers "where did the work go?": projects × days, colored by
 * turns per day (quartile scale), with row totals. Top projects get rows;
 * the tail lumps into "Other (N projects)".
 */
export function WorkHeatmap({ data, loading }: { data?: GetProjectHeatmapResponse; loading: boolean }) {
  const cells = data?.cells ?? [];

  // Distinct sorted day columns.
  const daySet = new Set<number>();
  for (const c of cells) daySet.add(parseInt(c.date ?? '0', 10));
  const days = [...daySet].sort((a, b) => a - b);

  // Group by project; rank; lump the tail.
  const byProject = new Map<string, Map<number, number>>();
  for (const c of cells) {
    const p = c.project ?? '';
    if (!byProject.has(p)) byProject.set(p, new Map());
    const day = parseInt(c.date ?? '0', 10);
    const m = byProject.get(p)!;
    m.set(day, (m.get(day) ?? 0) + (parseInt(c.turns ?? '0', 10) || 0));
  }
  const ranked = [...byProject.entries()]
    .map(([project, byDay]) => ({
      project,
      byDay,
      total: [...byDay.values()].reduce((a, b) => a + b, 0),
    }))
    .sort((a, b) => b.total - a.total);

  const rows: Row[] = ranked.slice(0, MAX_PROJECT_ROWS).map((r) => ({ ...r, isOther: false, otherCount: 0 }));
  const tail = ranked.slice(MAX_PROJECT_ROWS);
  if (tail.length > 0) {
    const other = new Map<number, number>();
    let total = 0;
    for (const r of tail) {
      total += r.total;
      for (const [d, t] of r.byDay) other.set(d, (other.get(d) ?? 0) + t);
    }
    rows.push({ project: '', isOther: true, otherCount: tail.length, byDay: other, total });
  }

  const allValues = rows.flatMap((r) => days.map((d) => r.byDay.get(d) ?? 0));
  const thresholds = computeThresholds(allValues);

  const fmtDay = (unix: number) => {
    const d = new Date(unix * 1000);
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}`;
  };

  return (
    <Card>
      <Card.Body>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-baseline gap-3">
            <Eyebrow>Where the work went</Eyebrow>
            <span className={cx('text-sm', text.muted)}>Turns per day</span>
          </div>
          <HeatmapLegend />
        </div>

        {loading && !data && <div className="mt-4 h-40 animate-pulse rounded bg-gray-100 dark:bg-navy-800" />}

        {!loading && rows.length === 0 && (
          <p className={cx('mt-4 py-6 text-center text-sm', text.secondary)}>No activity in this period.</p>
        )}

        {rows.length > 0 && (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full border-separate" style={{ borderSpacing: '3px 4px' }}>
              <caption className="sr-only">Turns per day by project</caption>
              <thead>
                <tr>
                  <th scope="col" className="sr-only">
                    Project
                  </th>
                  {days.map((d) => (
                    <th key={d} scope="col" className={cx('text-center text-xs font-normal', text.muted)}>
                      {fmtDay(d)}
                    </th>
                  ))}
                  <th scope="col" className={cx('pl-2 text-right text-xs font-medium uppercase', text.muted)}>
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const label = r.isOther ? `Other (${r.otherCount} ${r.otherCount === 1 ? 'project' : 'projects'})` : r.project || 'unknown';
                  return (
                    <tr key={r.isOther ? '__other' : r.project}>
                      <th
                        scope="row"
                        className={cx(
                          'max-w-48 truncate pr-2 text-left text-sm font-normal',
                          r.isOther ? cx('italic', text.muted) : text.primary,
                        )}
                      >
                        {r.isOther ? (
                          label
                        ) : (
                          <Link
                            to={`/sessions?project=${encodeURIComponent(r.project || 'unknown')}`}
                            className={text.link}
                          >
                            {r.project || 'unknown'}
                          </Link>
                        )}
                      </th>
                      {days.map((d) => {
                        const v = r.byDay.get(d) ?? 0;
                        const cellLabel = `${label} · ${fmtDay(d)}: ${v.toLocaleString('en-US')} turns`;
                        return (
                          <td
                            key={d}
                            className={cx('h-7 min-w-10 rounded-md', LEVEL_CLASSES[getLevel(v, thresholds)])}
                            title={cellLabel}
                            aria-label={cellLabel}
                          />
                        );
                      })}
                      <td className={cx('pl-2 text-right font-mono text-sm tabular-nums', text.primary)}>
                        {r.total.toLocaleString('en-US')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
